<template>
  <v-container class="hyp-pt-container">
    <v-row class="text-left mb-2">
      Targets are used to send notifications to 3rd party services such as
      Slack, WhatsApp and IFTTT. To create a new target click on the Add New
      Target button below and fill out the form that appears.
    </v-row>
    <v-row justify="start">
      <v-btn
        :disabled="loading"
        color="primary"
        type="button"
        small
        @click="handleClickAddNewTarget"
      >
        Add New Target
      </v-btn>
    </v-row>
    <v-row>
      <Loader
        v-if="loading"
        color="grey"
        text="Loading targets"
        size="small"
      ></Loader>
    </v-row>
    <v-row class="mt-5">
      <v-expansion-panels
        v-if="!loading && targets && targets.length"
        class="hyp-panels"
      >
        <v-expansion-panel v-for="target in targets" :key="target.id">
          <v-expansion-panel-header class="pl-1 pr-3">
            <template v-slot:default="{}">
              <v-row no-gutters>
                <v-col cols="2" class="text-center">
                  <v-icon v-if="target.icon" size="16">{{
                    target.icon
                  }}</v-icon>
                  <span v-if="!target.icon" class="target-type-header">{{
                    target.targetType
                  }}</span>
                </v-col>
                <v-col cols="10">
                  {{ target.name }}
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="target">
              <v-divider></v-divider>
              <v-card-text>
                <div>
                  <span class="application-heading mr-1">
                    Type:
                  </span>
                  <span class="target-type">
                    {{ target.targetType }}
                  </span>
                </div>
                <div v-for="field in target.fields" :key="field.key">
                  <span class="application-heading mr-1">
                    {{ field.label }}:
                  </span>
                  {{ target.options[field.key] }}
                </div>
                <div v-if="target.updatedTime">
                  <span class="application-heading mr-1">
                    Last updated:
                  </span>
                  <span>
                    {{ getFormattedDateTime(target.updatedTime) }}
                  </span>
                </div>

                <v-alert
                  v-if="testTargetResponse"
                  border="left"
                  colored-border
                  type="info"
                  elevation="2"
                  class="mt-3 mb-0 hyp-alert-target application-heading"
                >
                  A test message has been sent to your target
                </v-alert>

                <v-row>
                  <v-col class="mt-3 pb-0">
                    <v-btn
                      color="primary"
                      type="button"
                      outlined
                      small
                      class="mr-2"
                      @click="handleClickConfirmDelete(target)"
                      >Delete</v-btn
                    >
                    <v-btn
                      color="primary"
                      type="button"
                      outlined
                      small
                      @click="handleClickTest(target)"
                      >Test
                      <v-progress-circular
                        v-if="loadingTest"
                        indeterminate
                        size="14"
                        width="2"
                        class="ml-2"
                      ></v-progress-circular>
                    </v-btn>
                    <v-btn
                      color="primary"
                      type="button"
                      small
                      class="pull-right"
                      @click="editTarget(target)"
                      >Edit</v-btn
                    >
                    <div class="clearfix"></div>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-row>

    <v-dialog v-if="targetToDelete" v-model="showConfirmDelete" width="500">
      <v-card>
        <v-card-title>
          Delete Target?
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete the target: {{ targetToDelete.name }}?
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" small text @click="handleClickDeleteCancel"
            >Cancel</v-btn
          >
          <v-btn color="primary" small @click="handleClickDelete"
            >Yes, delete it
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showTargetForm" width="800">
      <v-card>
        <v-card-title class="headline">
          {{ formActionText }} Target
        </v-card-title>
        <v-card-text>
          <v-select
            :value="target.targetType"
            :items="targetTypes"
            item-text="display"
            item-value="targetType"
            label="Target Type"
            :disabled="!!target.targetId"
            @change="handleChangeTargetTypes"
          ></v-select>
          <v-text-field
            label="Target Name"
            v-model="target.name"
            @blur="modifyTargetName"
          ></v-text-field>

          <!-- TYPE SPECIFIC FIELDS -->
          <div v-for="field in fields" :key="field.key">
            <v-text-field
              v-model="target.options[field.key]"
              :label="field.label"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-text>
          <v-alert
            v-if="testTargetResponse"
            border="left"
            colored-border
            type="info"
            elevation="2"
            class="my-0 application-heading"
          >
            A test message has been sent to your target
          </v-alert>
          <v-alert
            v-if="errorMessage"
            border="left"
            colored-border
            type="error"
            elevation="2"
            class="my-0 application-heading"
          >
            {{ errorMessage }}
          </v-alert>
          <v-alert
            v-if="!!validation && !!validation.length"
            border="left"
            colored-border
            type="error"
            elevation="2"
            class="my-0 application-heading"
          >
            {{ validation[0] }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="primary" text @click="handleClickCancel">Cancel</v-btn>
          <v-btn
            color="primary"
            outlined
            :disabled="!!validation && !!validation.length"
            @click="handleClickTest"
            >Test
            <v-progress-circular
              v-if="loadingTest"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!!validation && !!validation.length"
            @click="handleClickSave"
            >Save
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";
import lodashOrderBy from "lodash/orderBy";

import {
  saveTarget,
  testTarget,
  getTargets,
  deleteTarget,
} from "@/services/targets/target.service.js";
import { TARGETS_TYPES } from "@/common/constants/targets";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { getFormatted } from "@/common/date.utilities";

export default {
  name: "PersonalTargetsSelector",
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const targetTypes = TARGETS_TYPES;
    return {
      targetTypes: targetTypes,
      defaultType: targetTypes[0],
      showTargetForm: false,
      target: targetTypes[0],
      targets: [],
      testTargetResponse: null,
      showConfirmDelete: false,
      targetToDelete: {},
      show: {},
      errorMessage: null,
      nameTag: "[Personal] ",
      loading: false,
      loadingTest: false,
      fields: [],
    };
  },
  async mounted() {
    await this.getPersonalTargets();
  },
  computed: {
    formActionText() {
      if (lodashGet(this.target, "targetId")) {
        return "Update";
      }
      return "Add";
    },
    validation() {
      const arr = [];
      if (!lodashGet(this.target, "name")) {
        arr.push("Target Name is required");
      }
      if (lodashGet(this.target, "targetType")) {
        const target = lodashFind(this.targetTypes, [
          "targetType",
          this.target.targetType,
        ]);
        target.fields.forEach((field) => {
          if (lodashGet(field, "validations.length")) {
            field.validations.forEach((validation) => {
              if (validation.func && validation.text) {
                if (typeof validation.func === "function") {
                  const validated = validation.func(
                    this.target.options[field.key]
                  );
                  if (!validated) {
                    arr.push(`${field.label} ${validation.text}`);
                  }
                }
              }
            });
          }
        });
      }
      return arr;
    },
  },
  methods: {
    handleClickAddNewTarget() {
      this.target = lodashCloneDeep(this.defaultType);
      this.getTargetFields();
      this.showTargetForm = true;
    },
    handleChangeTargetTypes(value) {
      this.target.targetType = value;
      this.getTargetFields();
    },
    handleClickCancel() {
      this.closeForm();
    },
    getTargetFields() {
      if (lodashGet(this.target, "targetType")) {
        const target = lodashFind(this.targetTypes, [
          "targetType",
          this.target.targetType,
        ]);
        if (lodashGet(target, "fields")) {
          this.fields = target.fields;
        }
      }
    },
    getFormattedDateTime(isoDateTime) {
      if (isoDateTime) {
        return getFormatted(isoDateTime);
      }
      return null;
    },
    async closeForm() {
      this.errorMessage = null;
      this.target = lodashCloneDeep(this.defaultType);
      this.showTargetForm = false;
      await this.getPersonalTargets();
    },
    async getPersonalTargets() {
      this.loading = true;
      this.targets = await getTargets();
      if (lodashGet(this.targets, "length")) {
        this.targets.forEach((target) => {
          const t = lodashFind(this.targetTypes, [
            "targetType",
            target.targetType,
          ]);
          if (t && t.fields) {
            target.fields = t.fields;
          }
        });
        this.targets = lodashOrderBy(this.targets, ["updatedTime"], ["desc"]);
      }
      this.loading = false;
    },
    async handleClickSave() {
      this.loading = true;
      await saveTarget(this.target);
      this.loading = false;
      this.closeForm();
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.personalTargets,
        EVENT_ACTIONS.clicked,
        "Save a Personal Target"
      );
    },
    async handleClickTest(target) {
      const t = target && target.targetId ? target : this.target;
      this.loadingTest = true;
      this.testTargetResponse = await testTarget(t);
      this.loadingTest = false;
      lodashDebounce(() => {
        this.testTargetResponse = false;
      }, 4000)();
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.personalTargets,
        EVENT_ACTIONS.clicked,
        "Testing a Personal Target"
      );
    },
    async editTarget(target) {
      this.target = target;
      this.getTargetFields();
      this.showTargetForm = true;
    },
    handleClickConfirmDelete(target) {
      this.showConfirmDelete = true;
      this.targetToDelete = target;
    },
    async handleClickDelete() {
      this.loading = true;
      const target = this.targetToDelete;
      if (target) {
        await deleteTarget(target);
      }
      this.loading = false;
      this.showConfirmDelete = false;
      await this.getPersonalTargets();
    },
    handleClickDeleteCancel() {
      this.showConfirmDelete = false;
      this.targetToDelete = null;
    },
    modifyTargetName(e) {
      this.target.name =
        this.target.name &&
        this.target.name.indexOf(this.nameTag) === -1 &&
        e.code !== "Backspace"
          ? this.nameTag + this.target.name
          : this.target.name;
    },
  },
};
</script>

<style>
.hyp-alert-target .v-alert__content {
  font-size: 12px;
}
</style>

<style scoped>
.target {
  text-align: left;
}
.v-application ol {
  list-style-type: none;
  padding: 0;
}
.hyp-pt-container {
  width: 400px;
  min-height: 500px;
}
.target-type {
  text-transform: uppercase;
}
.target-type-header {
  text-transform: uppercase;
  font-size: 10px;
}
</style>
